import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';
import useAnalyzing from '../utils/useAnalyzing';
import socket from '../socket';
import VerdictBar, { getVerdict } from '../components/VerdictBar';
import IncidencesList from '../components/IncidencesList';
import CategoryPill from '../components/CategoryPill';
import ShareButton from '../components/ShareButton';
import { buildReplyGroups } from '../utils/replyGroups';

// Match ReplyCard's REPLY_STATUS_BG (PostDetail.js:2823) — same tokens so the
// incident-as-reply card visually matches a ReplyCard rendering. Full ReplyCard
// extraction was scoped out (PostDetail.js:3019 has ~500 lines with 7+ tightly-
// coupled sub-components); we mimic its chrome here.
const STATUS_BG = {
  green:    'var(--color-verified-bg)',
  red:      'var(--color-invalidated-bg)',
  neutral:  'var(--color-surface)',
  disputed: 'var(--color-disputed-bg, rgba(217,119,6,0.10))',
};
const STATUS_LEFT_BORDER = {
  green:    'var(--color-verified)',
  red:      'var(--color-invalidated)',
  neutral:  'var(--color-border)',
  disputed: 'var(--color-disputed, #d97706)',
};

function StancePill({ stance, argument_type }) {
  if (argument_type === 'evidence_challenge') {
    return (
      <span style={{
        fontSize: 10, fontWeight: 700, color: '#92400e', background: '#fef3c7',
        padding: '2px 8px', borderRadius: 8, textTransform: 'uppercase',
        letterSpacing: '0.05em', fontFamily: 'var(--font-body)',
      }}>
        Evidence challenge
      </span>
    );
  }
  const isAff = stance === 'affirmative';
  const color = isAff ? 'var(--color-verified)' : 'var(--color-invalidated)';
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, color,
      textTransform: 'uppercase', letterSpacing: '0.05em',
      fontFamily: 'var(--font-body)',
    }}>
      {isAff ? 'Affirmative' : 'Rebuttal'}
    </span>
  );
}

function CardChrome({ status, depth = 0, children }) {
  const bg = STATUS_BG[status] || STATUS_BG.neutral;
  return (
    <div style={{
      background: bg,
      border: '1px solid var(--color-border)',
      borderLeft: `3px solid ${STATUS_LEFT_BORDER[status] || STATUS_LEFT_BORDER.neutral}`,
      borderRadius: 'var(--radius-card, 8px)',
      padding: '12px 14px',
      marginBottom: 10,
      marginLeft: depth > 0 ? 16 : 0,
    }}>
      {children}
    </div>
  );
}

function ReplyMimic({ reply, children, depth }) {
  return (
    <CardChrome status={reply.status} depth={depth}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <StancePill stance={reply.stance} argument_type={reply.argument_type} />
        {reply.author_username && (
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
            @{reply.author_username}
          </span>
        )}
      </div>
      <p style={{
        margin: 0, fontSize: 13, color: 'var(--color-text-primary)',
        fontFamily: 'var(--font-body)', lineHeight: 1.5,
      }}>
        {reply.synopsis_medium || reply.synopsis}
      </p>
      {children && (
        <div style={{ marginTop: 10 }}>{children}</div>
      )}
    </CardChrome>
  );
}

function buildReplyTree(replies, rootId, startDepth = 0) {
  const byParent = new Map();
  for (const r of replies) {
    const p = r.parent_node_id;
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p).push(r);
  }
  function render(parentId, depth) {
    const kids = byParent.get(parentId) || [];
    if (kids.length === 0) return null;
    return kids.map(k => (
      <ReplyMimic key={k.id} reply={k} depth={depth}>
        {render(k.id, depth + 1)}
      </ReplyMimic>
    ));
  }
  return render(rootId, startDepth);
}

// Inline reply composer — sub-sprint 5 dual-write. Posts to the same endpoint
// as PostDetail's main composer, with parent_argument_id pre-set to the incident
// node id. The AI is still sole authority on classification; this is just the
// composition context. Image upload supported via /api/upload (same shape as
// PostDetail's ReplyBox).
//
// Visual deliberately compact — IncidentDetail is one thread, not a full feed.
// Full ReplyBox (PostDetail.js:991) is ~1100 lines with URL scrape preview,
// link-only validation, char counters etc. — out of scope to duplicate. The
// text + image + submit primitives are enough for sub-sprint 5; the backend
// pipeline handles everything else.
function IncidentReplyComposer({ rootPostId, incidentNodeId, onSubmitted }) {
  const [content, setContent]       = useState('');
  const [expanded, setExpanded]     = useState(false);
  const [imageFile, setImageFile]   = useState(null);
  const [imagePreview, setImagePrev] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');
  const fileInputRef = useRef(null);

  const clearImage = () => {
    setImageFile(null);
    setImagePrev(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImageChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setImageFile(f);
    const reader = new FileReader();
    reader.onload = ev => setImagePrev(ev.target.result);
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim() && !imageFile) return;
    if (!rootPostId || !incidentNodeId) {
      setError('Missing post context — cannot submit');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      let imageData = {};
      if (imageFile) {
        const fd = new FormData();
        fd.append('image', imageFile);
        const up = await api.post('/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        imageData = {
          image_url:        up.data.url,
          image_public_id:  up.data.public_id,
          image_ai_score:   up.data.ai_detection?.ai_score ?? null,
          image_ai_flagged: up.data.ai_detection?.is_flagged ?? false,
        };
      }
      await api.post(`/posts/${rootPostId}/respond`, {
        content,
        parent_argument_id: incidentNodeId,
        ...imageData,
      });
      setContent('');
      clearImage();
      setExpanded(false);
      onSubmitted && onSubmitted();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      style={{
        marginLeft: 16,
        borderLeft: '2px solid var(--color-border)',
        paddingLeft: 12,
        marginBottom: 10,
      }}
    >
      <div style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 6,
        padding: '6px 10px',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}>
        <textarea
          rows={expanded ? 3 : 1}
          style={{
            background: 'transparent', border: 'none', outline: 'none',
            color: 'var(--color-text-primary)', fontSize: 14, resize: 'none',
            width: '100%', lineHeight: 1.5, fontFamily: 'var(--font-body)',
            height: expanded ? 80 : 28, transition: 'height 0.15s',
          }}
          placeholder="Reply to this incident..."
          value={content}
          onChange={e => { setContent(e.target.value); if (e.target.value) setExpanded(true); }}
          onFocus={() => setExpanded(true)}
        />
        {imagePreview && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <img src={imagePreview} alt="preview" style={{ height: 36, width: 48, objectFit: 'cover', borderRadius: 3, border: '1px solid var(--color-border)' }} />
            <button
              type="button" onClick={clearImage}
              style={{
                background: 'var(--color-border)', border: 'none', borderRadius: '50%',
                color: 'var(--color-text-primary)', width: 18, height: 18, cursor: 'pointer',
                fontSize: 11, lineHeight: 1,
              }}
            >×</button>
          </div>
        )}
        {(expanded || content || imageFile) && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <label style={{ cursor: 'pointer', fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)' }}>
              📎 Image
              <input
                ref={fileInputRef}
                type="file" accept="image/*" onChange={handleImageChange}
                style={{ display: 'none' }}
              />
            </label>
            <button
              type="submit"
              disabled={submitting || (!content.trim() && !imageFile)}
              style={{
                padding: '4px 12px',
                background: 'var(--color-brand)',
                color: '#fff', border: 'none', borderRadius: 4,
                cursor: submitting ? 'wait' : 'pointer',
                fontSize: 12, fontWeight: 600,
                fontFamily: 'var(--font-body)',
                opacity: (submitting || (!content.trim() && !imageFile)) ? 0.6 : 1,
              }}
            >
              {submitting ? 'Submitting…' : 'Reply'}
            </button>
          </div>
        )}
        {error && (
          <p style={{ color: 'var(--color-invalidated)', fontSize: 12, margin: 0, fontFamily: 'var(--font-body)' }}>
            {error}
          </p>
        )}
      </div>
    </form>
  );
}

export default function IncidentDetail({ user }) {
  const { nodeId } = useParams();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const fetchIncident = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    api.get(`/incidents/${nodeId}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(err => {
        setError(err.response?.data?.error || 'Failed to load incident');
        setLoading(false);
      });
  }, [nodeId]);

  useEffect(() => { fetchIncident(); }, [fetchIncident]);

  // Sprint A (Bug 3b fix): IncidentDetail had zero socket subscriptions, so
  // replies posted by anyone else (or even by this user from a separate tab)
  // never appeared until manual refresh. Mirror PostDetail's pattern —
  // join the post room once the parent_argument's post_id is known from the
  // first fetch, then refetch on debate_updated/feed_updated for that postId.
  const rootPostId = data?.incident?.parent_argument?.post_id || null;
  const { analyzing, start: startAnalyzing, done: doneAnalyzing } = useAnalyzing(() => fetchIncident(true));
  useEffect(() => {
    if (!rootPostId) return;
    const postIdNum = parseInt(rootPostId, 10);
    const onDebateUpdated = ({ postId: updatedId }) => {
      if (String(updatedId) !== String(postIdNum)) return;
      doneAnalyzing();
      fetchIncident(true);
    };
    const onFeedUpdated = ({ postId: fPostId }) => {
      if (String(fPostId) !== String(postIdNum)) return;
      fetchIncident(true);
    };
    socket.on('debate_updated', onDebateUpdated);
    socket.on('feed_updated', onFeedUpdated);
    socket.emit('join_post', postIdNum);
    const onReconnect = () => {
      socket.emit('join_post', postIdNum);
      fetchIncident(true);
    };
    socket.on('connect', onReconnect);
    return () => {
      socket.emit('leave_post', postIdNum);
      socket.off('debate_updated', onDebateUpdated);
      socket.off('feed_updated', onFeedUpdated);
      socket.off('connect', onReconnect);
    };
  }, [rootPostId, fetchIncident, doneAnalyzing]);

  if (loading) return (
    <div style={{ padding: 32, color: 'var(--color-text-muted)', fontFamily: 'var(--font-body)', fontSize: 14 }}>
      Loading…
    </div>
  );
  if (error || !data) return (
    <div style={{ padding: 32, color: 'var(--color-invalidated)', fontFamily: 'var(--font-body)', fontSize: 14 }}>
      {error || 'Incident not found'}
    </div>
  );

  const { incident, sources, replies } = data;
  const backHref = rootPostId ? `/posts/${rootPostId}` : '/';
  const backLabel = incident.parent_argument?.synopsis_short
    || incident.parent_argument?.synopsis
    || 'post';

  const verdict = getVerdict(incident.status, false);
  const displayVerdict = verdict === 'new' ? 'neutral' : verdict;

  // Sprint A Phase 2: top-level replies → grouped buckets; descendants
  // continue to nest under each top-level card via buildReplyTree. Same
  // pattern PostDetail uses (top-level grouped via ReplyGroupList; subtree
  // recursive via ReplyTree).
  const repliesArr = replies || [];
  const topLevelReplies = repliesArr.filter(r => String(r.parent_node_id) === String(incident.id));
  const groups = buildReplyGroups(topLevelReplies);

  const shareTitle = incident.synopsis_medium || incident.synopsis || 'Incident';
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '16px 16px 64px' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, marginBottom: 16, flexWrap: 'wrap',
      }}>
        <Link
          to={backHref}
          style={{
            display: 'inline-block',
            fontSize: 13, color: 'var(--color-brand)',
            textDecoration: 'none', fontFamily: 'var(--font-body)',
          }}
        >
          ← Back to {backLabel}
        </Link>
        <ShareButton url={shareUrl} title={shareTitle.slice(0, 80)} />
      </div>

      {/* Incident as a ReplyCard-style card with sources inline as Supporting
          Evidence — matches the visual structure established by the layout
          fix sprint (2026-05-18). */}
      <div style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-card, 10px)',
        overflow: 'hidden',
        boxShadow: 'var(--shadow-card)',
        marginBottom: 10,
      }}>
        <VerdictBar verdict={displayVerdict} />
        <div style={{ padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 10, fontWeight: 700, color: 'var(--color-text-muted)',
              fontFamily: 'var(--font-body)', textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>
              Incident
            </span>
            <StancePill stance={incident.stance} argument_type={incident.argument_type} />
            <CategoryPill label={incident.category_label} />
          </div>
          <p style={{
            margin: 0, fontSize: 15, color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-body)', lineHeight: 1.55,
          }}>
            {incident.synopsis_medium || incident.synopsis}
          </p>
          {incident.status_reasoning && (
            <p style={{
              margin: '10px 0 0', fontSize: 12, color: 'var(--color-text-muted)',
              fontFamily: 'var(--font-body)', lineHeight: 1.45, fontStyle: 'italic',
            }}>
              {incident.status_reasoning}
            </p>
          )}

          {/* Sprint A Phase 2 (Fix 4): incidences list (chip + status + click
              to evidence detail). The incident page surfaces evidence rows
              under a "Supporting Evidence" header for clarity even though
              IncidencesList defaults to "Incidences". includeUrlless=true so
              claim-only exhibits appear (incidents often cite witness
              statements without a URL). The sources rows come from the
              /incidents/:nodeId endpoint and carry an `id` so chips become
              real links to /posts/:postId/evidence/:id. */}
          {sources && sources.length > 0 && (
            <div style={{
              marginTop: 14, borderTop: '1px solid #f1f5f9', paddingTop: 12,
            }}>
              <IncidencesList
                evidence={sources}
                postId={rootPostId}
                label="Supporting Evidence"
                includeUrlless={true}
              />
            </div>
          )}
        </div>
      </div>

      {/* Composer + discussion replies as nested children of the incident card.
          Matches PostDetail's ReplyTree-under-ReplyCard pattern: marginLeft +
          borderLeft, no separate "DISCUSSION (N)" header. Composer renders
          only when authenticated; matches PostDetail's hide-if-no-user
          pattern (no inline login prompt — auth happens at the page level). */}
      {user && rootPostId && (
        <IncidentReplyComposer
          rootPostId={rootPostId}
          incidentNodeId={incident.id}
          onSubmitted={() => { startAnalyzing(); fetchIncident(true); }}
        />
      )}
      {analyzing && (
        <div aria-live="polite" style={{ marginLeft: 16, marginBottom: 10, fontSize: 13, color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>
          Analyzing your reply. It will appear here in about 20 seconds.
        </div>
      )}

      {/* Sprint A Phase 2: grouped top-level + nested descendants.
          Mirrors PostDetail's ReplyGroupList: each bucket has a collapsible
          header showing item count; each top-level card nests its subtree
          via buildReplyTree starting from that item's id. */}
      {groups.map(group => (
        <IncidentReplyGroup
          key={group.key}
          group={group}
          allReplies={repliesArr}
          storageKey={`incident-group-collapsed-${incident.id}-${group.key}`}
        />
      ))}
    </div>
  );
}

function IncidentReplyGroup({ group, allReplies, storageKey }) {
  const [collapsed, setCollapsed] = useState(() => {
    if (group.key === 'definitions') return false;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored !== null) return stored === 'true';
    } catch {}
    return !group.defaultExpanded;
  });
  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(storageKey, String(next)); } catch {}
  };
  return (
    <div style={{ marginBottom: 12 }}>
      <button
        type="button"
        onClick={toggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          width: '100%',
          background: 'transparent', border: 'none',
          padding: '8px 4px',
          fontSize: 12, fontWeight: 600,
          color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-body)',
          textTransform: 'uppercase', letterSpacing: '0.05em',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span>{group.icon}</span>
        <span>{group.title}</span>
        <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>({group.items.length})</span>
        <span style={{ marginLeft: 'auto', fontSize: 14 }}>{collapsed ? '▸' : '▾'}</span>
      </button>
      {!collapsed && group.items.map(item => (
        <ReplyMimic key={item.id} reply={item} depth={0}>
          {buildReplyTree(allReplies, item.id, 1)}
        </ReplyMimic>
      ))}
    </div>
  );
}
